import { inject, Injector, runInInjectionContext, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';

import { combineLatest, distinctUntilChanged, map, Observable, shareReplay, switchMap } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { injectReportsTranslations } from '@core/reports-contracts';
import {
  ReportDashboardComponentViewData,
  ReportsComponentValueDecorator,
} from '@core/reports-types';
import { buildVisualizationData } from '@core/reports-ui';
import { buildApiDataParams } from '@core/reports-util';
import { DurationPipe } from '@core/user-interface-legacy';

import { ReportsConfigurationFacade } from './reports-configuration.facade';
import { ReportsResultsFacade } from './results.facade';

export const useReportsComponentFullDataStreams = (
  component$: Observable<ReportDashboardComponentViewData>,
  options?: {
    componentDecorator?: ReportsComponentValueDecorator;
  },
) => {
  const { componentDecorator } = options ?? {};
  const resultsFacade = inject(ReportsResultsFacade);
  const sanitizer = inject(DomSanitizer);
  const translations = injectReportsTranslations();
  const durationPipe = new DurationPipe(translations.i18n);
  const configFacade = inject(ReportsConfigurationFacade);
  const metric$ = component$.pipe(
    filterBoolean,
    switchMap(component => configFacade.getMetricBySlug(component.metric)),
    shareReplay(1),
  );

  const params$ = combineLatest([component$, metric$]).pipe(
    map(([component, metric]) => buildApiDataParams(component, metric)),
    distinctUntilChanged(resultsFacade.areParamsEqual),
    shareReplay(1),
  );

  const results$ = params$.pipe(
    switchMap(params => resultsFacade.fetchAndGetResultsContinously$(params)),
    shareReplay(1),
  );

  const resultsMeta$ = params$.pipe(
    switchMap(params => resultsFacade.getResultsMeta(params)),
    shareReplay(1),
  );

  const visData$ = combineLatest([component$, metric$, results$]).pipe(
    map(([c, metric, results]) => {
      if (!results || !c || !metric) {
        return null;
      }
      const component = componentDecorator ? componentDecorator(c, metric) : c;
      const dataInput = {
        component,
        metric,
        meta: {},
        data: results,
      };
      return buildVisualizationData(dataInput, {
        sanitizer,
        translations,
        durationPipe,
      });
    }),
    shareReplay(1),
  );

  return {
    params$,
    results$,
    resultsMeta$,
    visData$,
  };
};

export const useReportsComponentFullData = (
  component: Signal<ReportDashboardComponentViewData | null>,
) => {
  const injector = inject(Injector);
  const component$ = toObservable(component).pipe(filterBoolean);
  const { params$, results$, resultsMeta$, visData$ } = runInInjectionContext(injector, () =>
    useReportsComponentFullDataStreams(component$),
  );

  return {
    params: toSignal(params$),
    results: toSignal(results$),
    resultsMeta: toSignal(resultsMeta$),
    visData: toSignal(visData$),
  };
};
